import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, PenLine, Check, ClipboardList, FileText, Download } from "lucide-react";
import StatusTimeline from "../components/StatusTimeline";
import NotesPanel from "../components/NotesPanel";
import SLABadge from "../components/SLABadge";
import CollapsiblePdfPreview from "../components/CollapsiblePdfPreview";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
}

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const loadJob = useCallback(async () => {
    try {
      const { data } = await api.get(`/job-cards/${jobId}`);
      setJob(data);
    } catch { toast.error("Failed to load job"); }
    setLoading(false);
  }, [jobId]);

  useEffect(() => { loadJob(); }, [loadJob]);

  // Signature canvas handlers
  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0D9488";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearSig = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  const handleComplete = async () => {
    if (!canvasRef.current) return;
    if (!hasSignature) {
      toast.error("Please sign in the box before completing the job");
      return;
    }
    setCompleting(true);
    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, "image/png")
      );
      if (!blob) {
        toast.error("Could not capture signature — please try signing again");
        setCompleting(false);
        return;
      }
      const formData = new FormData();
      formData.append("signature", blob, "signature.png");
      formData.append("_complete", "true");
      await api.put(`/job-cards/${jobId}/complete`, formData);
      toast.success("Job completed!");
      loadJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete job");
    }
    setCompleting(false);
  };

  if (loading) return <div className="text-slate-500 p-8">Loading...</div>;
  if (!job) return <div className="text-slate-500 p-8">Job not found</div>;

  const canComplete = user?.role && ["CI", "CM"].includes(user.role) && job.status === "DELIVERED";

  return (
    <div data-testid="job-detail-page" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
          className="text-slate-500 hover:text-slate-800">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
              <ClipboardList size={24} className="text-teal-600" /> {job.job_card_number}
            </h1>
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{job.status}</Badge>
            <SLABadge sla={job.sla} />
          </div>
        </div>
      </div>

      {/* Status Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <StatusTimeline jobCard={job} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Job Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-4">
              Job Details
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Model</span>
                <span className="text-slate-800">{job.model || "-"}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Store</span>
                <span className="text-slate-800">{job.store || "-"}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Area</span>
                <span className="text-slate-800">{job.area || "-"}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Job Type</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  job.job_type === "DELIVERY"
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : job.job_type === "COLLECTION"
                      ? "bg-purple-50 text-purple-700 border border-purple-200"
                      : "bg-slate-100 text-slate-400 border border-slate-200"
                }`} data-testid="job-detail-job-type">
                  {job.job_type || "Not set"}
                </span>
              </div>
              {job.collector && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Collector</span>
                  <span className="text-slate-800">{job.collector}</span>
                </div>
              )}
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Created By</span>
                <span className="text-slate-800">{job.created_by_name}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Created</span>
                <span className="text-slate-800">{formatDate(job.date_created)}</span>
              </div>
              {job.estimated_collection_date && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">Est. Collection</span>
                  <span className="text-slate-800">{formatDate(job.estimated_collection_date)}</span>
                </div>
              )}
              {job.tpa_reference_number && (
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 block">3PA Reference #</span>
                  <span className="text-slate-800 font-medium">{job.tpa_reference_number}</span>
                </div>
              )}
            </div>
          </div>

          {/* Completion Report */}
          {job.completion_report_url && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-teal-600" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Completion Report</p>
                    <p className="text-xs text-slate-400">CargoDash - Skyworth JC Completion Report</p>
                  </div>
                </div>
                <a
                  data-testid="download-completion-report"
                  href={`${API_URL}/api/files/${job.completion_report_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg transition-colors"
                >
                  <Download size={13} /> Download PDF
                </a>
              </div>
            </div>
          )}

          {/* PDF Viewer (collapsible) */}
          {job.pdf_url && (
            <CollapsiblePdfPreview
              pdfUrl={job.pdf_url}
              title="Original Skyworth Job Card"
              downloadFilename={job.original_filename || `${job.job_card_number || "JobCard"}.pdf`}
              testIdPrefix="original-jc"
              height={420}
            />
          )}

          {/* Signature Section */}
          {canComplete && (
            <div className="bg-white rounded-xl shadow-sm p-5" data-testid="signature-section">
              <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-4 flex items-center gap-2">
                <PenLine size={16} className="text-teal-600" /> Signature & Complete
              </h2>
              <canvas
                ref={initCanvas}
                data-testid="signature-canvas"
                className="signature-canvas w-full h-32 bg-slate-100"
                width={600}
                height={128}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
              <div className="flex gap-3 mt-3 items-center">
                <Button variant="outline" size="sm" onClick={clearSig}
                  className="border-slate-300 text-slate-500">Clear</Button>
                <Button
                  data-testid="complete-job-btn"
                  onClick={handleComplete}
                  disabled={completing || !hasSignature}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
                >
                  <Check size={14} className="mr-1" />
                  {completing ? "Completing..." : "Complete Job"}
                </Button>
                {!hasSignature && (
                  <span className="text-xs text-slate-400" data-testid="signature-hint">
                    Sign in the box above to enable completion
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Show signature if completed */}
          {job.signature_url && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="font-heading font-bold text-sm uppercase tracking-[0.15em] text-slate-700 mb-3">
                Signature
              </h2>
              <img
                data-testid="completed-signature"
                src={`${API_URL}/api/files/${job.signature_url}`}
                alt="Signature"
                className="bg-slate-100 rounded-xl border border-slate-300 max-h-32"
              />
            </div>
          )}
        </div>

        {/* Notes Panel */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <NotesPanel jobId={jobId} notes={job.notes || []} onNoteAdded={loadJob} />
        </div>
      </div>
    </div>
  );
}
